import { useState, useEffect, useContext, useMemo } from "react";
import { usePaymentClient } from "../PaymentClient";
import { Cancelable, CancellationToken, makeCancelable } from "../../utils";
import { useWebSocketClient } from "../../api/WebSocketClient";
import LoadingBox from "../../shared/LoadingBox";
import CustomerMembershipContext, {
  useCustomerMembershipContext,
} from "./CustomerMembershipContext";
import { useHistory } from "react-router-dom";
import NokPrice from "@teamscheme/nok-price";
import CustomerInfo from "./CustomerInfo";
import { ApiError, useSessionId } from "../../api/ApiClient";
import { Button } from "@teamscheme/button";
import { ProductInfo } from "./PaymentRoutes";
import MasterCardLogo from "../logos/MasterCardLogo";
import VisaLogo from "../logos/VisaLogo";
import {
  BankIdPayment,
  MembershipProductPayment,
  PaymentViewModel,
} from "../PaymentViewModel";
import { CheckIconCircle } from "@teamscheme/icons";
import Divider from "../Divider";
import {
  ContractType,
  ContractUpgradePaymentInitiatorViewModel,
} from "../../contract/model/ContractViewModel";
import styled from "styled-components";
import PaymentLogos from "./PaymentLogos";
import UpdateType from "../../contract/updates/UpdateType";
import {
  getPaymentParametersQuery,
  PaymentParameters,
} from "../PaymentParameters";
import { LinkBackLink } from "@teamscheme/page-navigation-footer";
import { ButtonLink } from "@teamscheme/button-link";
import VippsLogo from "../../shared/logos/VippsLogo";

export function getContractUpgradePaymentParameters(
  upgradePayment: ContractUpgradePaymentInitiatorViewModel,
  contractType: ContractType
): PaymentParameters | null {
  const { customer, paymentId: id, contractType: type } = upgradePayment;

  if (!customer || type !== contractType) return null;

  const { customerGuid } = customer;

  return { id, customerGuid };
}

export function getPaymentParameters(bankIdPayment: BankIdPayment) {
  if (!bankIdPayment) return {};

  const { customer, paymentId: id } = bankIdPayment;

  if (!customer) return {};

  const { customerGuid } = customer;

  return { id, customerGuid };
}

export function useRedirectToActivePayment() {
  const history = useHistory();
  const paymentContext = useContext(CustomerMembershipContext);
  if (!paymentContext) throw new Error("No PaymentContext found!");
  const { customer } = paymentContext;
  const [isLoading, setIsLoading] = useState(true);

  const { fetch, data: payments } = usePaymentClient<PaymentViewModel[]>();

  useEffect(() => {
    async function load(cancellationToken: CancellationToken) {
      setIsLoading(true);
      try {
        await fetch(
          {
            method: "GET",
            path: `payment?customer=${customer.id}`,
          },
          cancellationToken
        );
      } catch (error) {
        if (!cancellationToken.isCanceled) throw error;
      } finally {
        if (!cancellationToken.isCanceled) setIsLoading(false);
      }
    }

    const cancellationToken = { isCanceled: false };

    load(cancellationToken);

    return () => {
      cancellationToken.isCanceled = true;
    };
  }, [fetch, customer.id]);

  useEffect(() => {
    if (payments) {
      const processing = payments.filter((p) => isProcessingCheckout(p));

      if (processing.length) {
        console.log("redirect to processing", processing);
        history.replace(
          `/payment/${processing[0].paymentDetails.id}?customer=${customer.id}`
        );
      }

      const products = customer.products.filter((p) => !p.isValid);

      const completed = payments.filter((p) => {
        if (!isCheckoutCompleted(p)) return false;

        const { membershipProduct } = p.membershipProductPayment || {};

        const paidProducts = products.filter(
          (product) => membershipProduct && membershipProduct.id === product.id
        );

        return paidProducts.length;
      });

      if (completed.length) {
        console.log("redirect to completed", completed);
        history.replace(
          `/payment/${completed[0].paymentDetails.id}?customer=${customer.id}`
        );
      }
    }
  }, [payments, customer.id, customer.products, history]);

  return {
    isLoading,
  };
}

export const PAYMENT_STATUSES = {
  None: 0,
  Registered: 1,
  Reserved: 2,
  Paid: 4,
  Canceled: 8,
  Failed: 16,
  Rejected: 32,
  RefundRegistered: 64,
  Refunded: 128,
  RefundCanceled: 256,
  RefundFailed: 1024,
  WaitingFor3DSecure: 2048,
  Completing3DSecure: 4096,
};

export const CHECKOUT_STATUSES = {
  None: 0,
  Paid: 1,
  Refunded: 2,
  Canceled: 4,
};

export function isProcessingCheckout(payment: PaymentViewModel) {
  if (!payment.paymentDetails.checkoutOrder) return false;

  return payment.paymentDetails.checkoutOrder.status === CHECKOUT_STATUSES.None;
}

export function isCheckoutCanceled(payment: PaymentViewModel) {
  if (!payment.paymentDetails.checkoutOrder) return false;

  return (
    payment.paymentDetails.checkoutOrder.status === CHECKOUT_STATUSES.Canceled
  );
}

export function isCheckoutCompleted(payment: PaymentViewModel) {
  if (!payment.paymentDetails.checkoutOrder) return false;

  return payment.paymentDetails.checkoutOrder.status === CHECKOUT_STATUSES.Paid;
}

export function usePaymentUpdates(parameters: PaymentParameters | null) {
  const [payment, setPayment] = useState<PaymentViewModel | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { onOpen, onMessage, send } = useWebSocketClient();
  const sessionId = useSessionId();

  const { id } = parameters || {};
  const signature =
    parameters && "signature" in parameters ? parameters.signature : null;

  const subscribeData = useMemo(
    () =>
      id
        ? {
            paymentId: id,
            signature,
            sessionId,
          }
        : null,
    [id, signature, sessionId]
  );

  useEffect(() => {
    if (!subscribeData) {
      setIsSubscribed(false);
      return;
    }

    setIsSubscribed(true);

    const unsubscribe = onOpen(() =>
      send({
        action: "SubscribeToPayment",
        data: subscribeData,
      })
    );

    return unsubscribe;
  }, [subscribeData, send, onOpen]);

  useEffect(() => {
    const unsubscribe = onMessage((event) => {
      const { payment, updateType } = JSON.parse(event.data);
      if (updateType === UpdateType.Payment) {
        if (payment.paymentDetails.id === id) {
          setPayment(payment);
        }
      }
    });

    return () => unsubscribe();
  }, [onMessage, id]);

  return useMemo(() => ({ payment, isSubscribed }), [payment, isSubscribed]);
}

export function usePaymentStatus(parameters: PaymentParameters | null) {
  const { payment: updatedPayment, isSubscribed } =
    usePaymentUpdates(parameters);
  const { id } = parameters || {};
  const query = parameters ? getPaymentParametersQuery(parameters) : "";
  const { fetch, data } = usePaymentClient<PaymentViewModel>();

  const [payment, setPayment] = useState(updatedPayment);
  const [isLoading, setIsLoading] = useState(!!id);

  useEffect(() => setPayment(data), [data]);
  useEffect(() => {
    if (updatedPayment) setPayment(updatedPayment);
  }, [updatedPayment]);

  useEffect(() => {
    async function executePromise(
      cancelable: Cancelable<PaymentViewModel | undefined>
    ) {
      try {
        await cancelable.promise;
      } catch (e) {
        const error = e as { isCanceled: true } | ApiError;
        if (!("isCanceled" in error && error.isCanceled)) {
          console.log(`Error in GET payment/${id}`, error);
          throw error;
        }
      } finally {
        if (!cancelable.isCanceled) setIsLoading(false);
      }
    }

    if (!id) return;

    setIsLoading(true);

    const cancelable = makeCancelable(
      fetch({
        method: "GET",
        path: `payment/${id}?${query}`,
      })
    );

    executePromise(cancelable);

    return () => cancelable.cancel();
  }, [id, fetch, query]);

  return {
    payment,
    isLoading,
    isSubscribed,
  };
}

export const ProductPaymentOptions = styled.div`
  margin-bottom: 2em;
`;

export const PaymentDetails = styled.div`
  padding-top: 2em;
`;

export default function MembershipPaymentStatus({
  signature,
  id,
}: {
  signature: string;
  id: string;
}) {
  const { payment, isLoading } = usePaymentStatus({ signature, id });

  if (isLoading || !payment) return <LoadingBox />;

  return (
    <>
      <LinkBackLink to={`/payment?signature=${signature}`} />
      <PaymentDetails>
        <PaymentDetailsView data={payment} />
      </PaymentDetails>
    </>
  );
}

export function MembershipPaymentConfirmationText({
  membershipProductPayment: payment,
}: {
  membershipProductPayment?: MembershipProductPayment;
}) {
  const paymentContext = useContext(CustomerMembershipContext);
  if (!paymentContext) throw new Error("No PaymentContext found!");

  const { customer } = paymentContext;

  if (!payment) {
    return (
      <p>Du vil motta en bekreftelse for gjennomført betaling på e-post.</p>
    );
  }

  const { membershipProduct } = payment;

  const customerProduct = membershipProduct
    ? customer.products.filter((p) => p.id === membershipProduct.id)[0]
    : null;

  const startDate = customerProduct
    ? customerProduct.startDate
    : membershipProduct.startDate;

  const trialMembership = customer.products.filter(
    (p) => p.id === "6111" /* Prøvemedlemskap med veihjelp */
  )[0];

  return (
    <>
      <p>
        Ditt medlemskap er aktivt fra og med{" "}
        {new Date(startDate).toLocaleDateString()}.
      </p>
      {trialMembership &&
      trialMembership.validUntil &&
      new Date(trialMembership.validUntil) > new Date() ? (
        <p>Prøvemedlemskapet ditt er fortsatt gyldig.</p>
      ) : null}
    </>
  );
}

export function PaymentDetailsView({ data }: { data: PaymentViewModel }) {
  const { yearlyProducts: products, customerGuid } =
    useCustomerMembershipContext();

  if (isProcessingCheckout(data)) {
    const { paymentDetails, membershipProductPayment } = data;
    const { paymentUrl, amount } = paymentDetails;
    const product = products.find(
      (x) => x.id === membershipProductPayment?.membershipProduct.id
    );

    return (
      <div>
        <h1>Betaling</h1>
        {product ? (
          <ProductInfo product={product} />
        ) : (
          <p>
            <span>Betaling </span>
            {membershipProductPayment ? (
              <span>for {membershipProductPayment.membershipProduct.name}</span>
            ) : null}
            <span> hos Norges Automobil-Forbund.</span>
            <br />
            <NokPrice value={amount} />
          </p>
        )}
        {paymentUrl ? (
          <ProductPaymentOptions>
            <Button variant="secondary" block href={paymentUrl}>
              Gå til betaling
            </Button>
            <PaymentLogos>
              <VippsLogo />
              <VisaLogo />
              <MasterCardLogo />
            </PaymentLogos>
          </ProductPaymentOptions>
        ) : null}
        <hr />
        <CustomerInfo />
      </div>
    );
  }

  if (isCheckoutCompleted(data)) {
    return (
      <div>
        <p>
          <CheckIconCircle size={32} />
        </p>
        <h1>Betalingen er gjennomført!</h1>
        <MembershipPaymentConfirmationText
          membershipProductPayment={data.membershipProductPayment}
        />
        <br />
        <Divider />
        <CustomerInfo />
      </div>
    );
  }

  return (
    <div>
      <h1>Betalingen ble ikke gjennomført</h1>
      <p>
        Betalingen ble avbrutt, eller noe har gått galt. Åpne betaling på nytt
        for å fullføre.
      </p>
      <p>
        <br />
        <ButtonLink
          variant="secondary"
          linkTo={`/payment?customer=${customerGuid}`}
        >
          Tilbake til betaling
        </ButtonLink>
      </p>
    </div>
  );
}
